/**
 * 「画面越しの見かけの大きさ」を肉眼と一致させるための視角計算(計画書§2の体験品質)。
 *
 * スマホ画面はカメラFOV(例: 垂直70°)を物理的には十数cmの画面に圧縮して
 * 表示するため、画面内の物体は肉眼より大幅に小さく見える。
 * 目から距離Dの画面(物理高さH)が見込む角度は 2·atan(H/2D)。
 * 補正係数 k = カメラFOV ÷ 画面視角 をモデルの大きさに掛けると、
 * 画面上の見かけの視角が肉眼での視角と一致する。
 */

/**
 * 典型的なスマホのCSSピクセル密度[px/inch]。
 * ブラウザは物理サイズを直接提供しないため実測に基づく近似値を使う
 * (iPhone系: 390px/2.51in ≈ 155、Pixel系: 412px/2.7in ≈ 153)。
 */
const CSS_PX_PER_INCH = 155;

/** 腕を伸ばしてスマホを空に構えたときの、目から画面までの平均距離[cm] */
const EYE_DISTANCE_CM = 45;

export interface ViewAngleInfo {
  /** 画面(縦)が目に見込む角度[deg] */
  screenAngleDeg: number;
  /** 肉眼と同じ視角に見せるためのモデル拡大係数 */
  compensation: number;
}

/**
 * 現在の画面サイズとカメラ垂直FOVから視角補正係数を計算する。
 *
 * 透視投影では画面上の位置・大きさは角度ではなく tan(角度) に比例し、
 * 画面→目の関係も tan なので、画面中央付近の小さな物体に対する厳密な補正は
 *   k = 2·tan(FOV/2) · D / H   (D=目〜画面距離, H=画面物理高)
 * となる(角度比 FOV/画面視角 で近似すると約12%過小になる)。
 * この式は縦横どちらで計算しても同じ値になる(方向不変)。
 * デスクトップ等で極端な値になった場合に備えて[1, 8]にクランプする。
 */
export function computeViewAngle(cameraFovDeg: number): ViewAngleInfo {
  const screenHeightCm = (window.innerHeight / CSS_PX_PER_INCH) * 2.54;
  const screenAngleDeg =
    (2 * Math.atan(screenHeightCm / 2 / EYE_DISTANCE_CM) * 180) / Math.PI;
  const raw =
    (2 * Math.tan(((cameraFovDeg / 2) * Math.PI) / 180) * EYE_DISTANCE_CM) / screenHeightCm;
  const compensation = Math.min(8, Math.max(1, raw));
  return { screenAngleDeg, compensation };
}
